import { Phone, MapPinned, Video } from "lucide-react";

// Single source of truth for the Aktivitas Terjadwal types — shared by the Pipeline
// card badge, the Dashboard widget, and the board inside LeadDetailSheet's Aktivitas tab,
// so the icon/label/color for e.g. "survei" can never drift between the 3 places that
// render it.
//
// Follow Up lives here too, as a full board entry (create/edit/complete/cancel/delete,
// exactly like Survei/Zoom Meeting) — a prior version of this system deliberately left it
// out in favor of the plain lead.follow_up_date field, but that field alone has no "board"
// (no per-entry edit/delete, no history of multiple follow-ups). The two are now synced,
// not duplicated: the backend keeps lead.follow_up_date pointed at whichever pending
// follow_up-type entry is soonest (see _sync_lead_followup_date in server.py), so every
// existing consumer of follow_up_date (Pipeline board/calendar aging, the Konsultasi/SPH
// stage automation, the "Harus Di-Follow Up Hari Ini" Dashboard widget) keeps working
// unchanged — the board is just a richer way to manage the same underlying date.
export const SCHEDULED_ACTIVITY_TYPES = {
  follow_up: { label: "Follow Up", icon: Phone, accent: "#3b82f6" },
  survei: { label: "Survei", icon: MapPinned, accent: "#a855f7" },
  zoom_meeting: { label: "Zoom Meeting", icon: Video, accent: "#10b981" },
};

// Soonest still-pending scheduled activity for one lead, or null — filters to known types
// only, so any stray old-shape data (e.g. a since-removed type) never renders as a blank/
// broken badge, just gets skipped.
export function nextPendingActivity(lead) {
  const pending = (lead?.scheduled_activities || []).filter((a) => a.status === "pending" && SCHEDULED_ACTIVITY_TYPES[a.type]);
  if (!pending.length) return null;
  return pending.slice().sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
}

// One entry PER TYPE (soonest pending of each) — used for the Pipeline card badges, so a
// lead with both a Survei and a Zoom Meeting scheduled shows BOTH badges together instead
// of one silently replacing the other (that was the bug: picking only the single overall
// soonest across all types meant adding a second type's schedule made the first type's
// badge disappear from the card, even though it was still pending).
export function pendingActivitiesByType(lead) {
  const byType = {};
  for (const a of lead?.scheduled_activities || []) {
    if (a.status !== "pending" || !SCHEDULED_ACTIVITY_TYPES[a.type]) continue;
    if (!byType[a.type] || new Date(a.scheduled_at) < new Date(byType[a.type].scheduled_at)) byType[a.type] = a;
  }
  return Object.values(byType).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
}

// Every pending activity across a list of leads, flattened + annotated with the parent
// lead's id/nama/no_hp/sales_id so a widget can render+link back to the lead without a
// second lookup — used by the Dashboard's upcoming-activities widget. Sorted soonest first.
// Same known-type filter as nextPendingActivity, same reason.
export function flattenPendingActivities(leads) {
  const out = [];
  for (const l of leads || []) {
    for (const a of l.scheduled_activities || []) {
      if (a.status !== "pending" || !SCHEDULED_ACTIVITY_TYPES[a.type]) continue;
      out.push({ ...a, lead_id: l.lead_id, lead_nama: l.nama, lead_no_hp: l.no_hp, lead_status: l.status, sales_id: l.sales_id });
    }
  }
  out.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  return out;
}
